import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Activity, BookOpen, Bot, Loader2, CheckCircle2, XCircle, RefreshCw, Shield, Rocket, FolderSync, ChevronDown, Database, FileText, ScanSearch, CloudUpload } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useActivityStore, ActivityType, Activity as ActivityItem } from '@/stores/activityStore';
import { Button } from '@ncoder/ui';
import { useShallow } from 'zustand/react/shallow';

// Map activity types to icons and labels
const activityConfig: Record<ActivityType, { icon: React.ElementType; label: string; color: string }> = {
  metadata_sync_full: {
    icon: RefreshCw,
    label: 'Full Metadata Sync',
    color: 'text-blue-500',
  },
  metadata_sync_type: {
    icon: RefreshCw,
    label: 'Metadata Type Sync',
    color: 'text-cyan-500',
  },
  metadata_initial_sync: {
    icon: RefreshCw,
    label: 'Initial Metadata Fetch',
    color: 'text-indigo-500',
  },
  security_scan: {
    icon: Shield,
    label: 'Security Scan',
    color: 'text-purple-500',
  },
  deployment: {
    icon: Rocket,
    label: 'Deployment',
    color: 'text-green-500',
  },
  file_sync: {
    icon: FolderSync,
    label: 'File Sync',
    color: 'text-orange-500',
  },
  rag_ingest: {
    icon: Database,
    label: 'RAG Ingest',
    color: 'text-violet-500',
  },
  release_ingest: {
    icon: FileText,
    label: 'Release Doc Ingest',
    color: 'text-amber-500',
  },
  release_scan: {
    icon: ScanSearch,
    label: 'Release Impact Scan',
    color: 'text-rose-500',
  },
  release_pipeline: {
    icon: ScanSearch,
    label: 'Release Pipeline',
    color: 'text-rose-500',
  },
  agent_run: {
    icon: Bot,
    label: 'Agent Run',
    color: 'text-amber-500',
  },
  playbook_run: {
    icon: BookOpen,
    label: 'Migration Run',
    color: 'text-primary',
  },
  salesforce_push: {
    icon: CloudUpload,
    label: 'Salesforce Push',
    color: 'text-emerald-500',
  },
};

interface ActivityDropdownProps {
  className?: string;
}

export const ActivityDropdown: React.FC<ActivityDropdownProps> = ({ className }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  // Use shallow comparison to ensure re-renders when Map contents change
  const activitiesMap = useActivityStore(useShallow((state) => state.activities));
  
  // Convert Map to array, excluding completed activities
  const activeActivities = useMemo(
    () => Array.from(activitiesMap.values()).filter(a => a.status !== 'completed'),
    [activitiesMap]
  );
  const hasActiveActivities = activeActivities.length > 0;
  const inProgressCount = useMemo(
    () => activeActivities.filter(a => a.status === 'in_progress').length,
    [activeActivities]
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'in_progress':
        return <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />;
      case 'completed':
        return <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />;
      case 'failed':
        return <XCircle className="h-3.5 w-3.5 text-destructive" />;
      default:
        return null;
    }
  };

  const formatTimestamp = (date: Date) => {
    // Always show the absolute clock time (HH:MM:SS) — the old "Just now"
    // / "Xm ago" labels hid the actual second when refreshing rapidly, which
    // made it impossible to tell two back-to-back refreshes apart.
    return date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  return (
    <div ref={dropdownRef} className={cn('relative', className)}>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'relative gap-1.5 px-2 py-1.5 text-xs sm:text-sm',
          hasActiveActivities && inProgressCount > 0
            ? 'text-primary'
            : 'text-muted-foreground hover:text-foreground'
        )}
      >
        <div className="relative">
          <Activity className={cn(
            'h-4 w-4',
            inProgressCount > 0 && 'animate-pulse'
          )} />
          {inProgressCount > 0 && (
            <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-primary animate-pulse" />
          )}
        </div>
        <span className="hidden sm:inline">Activity</span>
        {inProgressCount > 0 && (
          <span className="inline-flex items-center justify-center h-4 min-w-4 px-1 text-[10px] font-bold bg-primary text-primary-foreground rounded-full">
            {inProgressCount}
          </span>
        )}
        <ChevronDown className={cn(
          'h-3 w-3 transition-transform',
          isOpen && 'rotate-180'
        )} />
      </Button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-1 w-72 sm:w-80 bg-popover border border-border rounded-lg shadow-lg z-50 overflow-hidden">
          <div className="px-3 py-2 border-b border-border bg-muted/30">
            <h3 className="text-sm font-medium">Activity</h3>
            <p className="text-xs text-muted-foreground">
              {inProgressCount > 0
                ? `${inProgressCount} process${inProgressCount > 1 ? 'es' : ''} running`
                : 'No active processes'}
            </p>
          </div>

          <div className="max-h-64 overflow-y-auto">
            {activeActivities.length === 0 ? (
              <div className="px-4 py-6 text-center">
                <Activity className="h-8 w-8 mx-auto text-muted-foreground/50 mb-2" />
                <p className="text-sm text-muted-foreground">No recent activity</p>
                <p className="text-xs text-muted-foreground/70 mt-1">
                  Activities like sync, deployment, and scans will appear here
                </p>
              </div>
            ) : (
              <div className="py-1">
                {activeActivities
                  .sort((a, b) => b.startedAt.getTime() - a.startedAt.getTime())
                  .map((activity: ActivityItem) => {
                    const config = activityConfig[activity.type];
                    const IconComponent = config.icon;
                    const metadataType = activity.metadata?.metadataType as string | undefined;
                    const agentName = activity.metadata?.agentName as string | undefined;
                    const skillTitle = activity.metadata?.skillTitle as string | undefined;
                    const pushBranchName = activity.metadata?.branchName as string | undefined;
                    const displayLabel =
                      activity.type === 'agent_run' && agentName
                        ? `Running ${agentName}`
                        : activity.type === 'playbook_run' && skillTitle
                          ? `Running ${skillTitle}`
                          : activity.type === 'metadata_sync_type' && metadataType
                            ? `Syncing ${metadataType}`
                            : activity.type === 'salesforce_push' && pushBranchName
                              ? `Pushing to ${pushBranchName}`
                              : config.label;
                    
                    return (
                      <div
                        key={activity.id}
                        className={cn(
                          'px-3 py-2.5 hover:bg-muted/50 transition-colors',
                          activity.status === 'in_progress' && 'bg-primary/5'
                        )}
                      >
                        <div className="flex items-start gap-2.5">
                          <div className={cn(
                            'flex-shrink-0 p-1.5 rounded-md',
                            activity.status === 'in_progress' ? 'bg-primary/10' : 'bg-muted'
                          )}>
                            <IconComponent className={cn('h-4 w-4', config.color)} />
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-sm font-medium truncate">
                                {displayLabel}
                              </span>
                              {getStatusIcon(activity.status)}
                            </div>
                            
                            {activity.message && (
                              <p className="text-xs text-muted-foreground truncate mt-0.5">
                                {activity.message}
                              </p>
                            )}
                            
                            {activity.status === 'in_progress' && activity.progress !== undefined && (
                              <div className="mt-1.5">
                                {/* No declared total: an animated sliver and a step count.
                                    Rendering `progress` here would print a confident "0%"
                                    for a run that has already finished several steps. */}
                                <div className="h-1 bg-muted rounded-full overflow-hidden">
                                  {activity.indeterminate ? (
                                    <div className="h-full w-1/3 animate-[pulse_1.6s_ease-in-out_infinite] rounded-full bg-primary/60" />
                                  ) : (
                                    <div
                                      className="h-full bg-primary rounded-full transition-all duration-300"
                                      style={{ width: `${Math.min(activity.progress, 100)}%` }}
                                    />
                                  )}
                                </div>
                                <span className="text-[10px] text-muted-foreground">
                                  {activity.indeterminate
                                    ? `${activity.completedSteps ?? 0} ${(activity.completedSteps ?? 0) === 1 ? 'step' : 'steps'} complete`
                                    : `${Math.round(activity.progress)}%`}
                                </span>
                              </div>
                            )}
                            
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-[10px] text-muted-foreground">
                                {formatTimestamp(activity.startedAt)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>

        </div>
      )}
    </div>
  );
};
